import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Plus, Trash2, KeyRound, Loader2, ShieldCheck, Eye, HardHat } from "lucide-react";

const ROLES = {
  admin: { label: "Administrador", cls: "bg-accent/10 text-accent border border-accent/30", icon: ShieldCheck, desc: "Edita todo, ajustes, copias y usuarios" },
  usuario: { label: "Usuario (solo lectura)", cls: "bg-slate-100 text-slate-700 border border-slate-200", icon: Eye, desc: "Ve toda la información sin poder editar" },
  trabajador: { label: "Trabajador", cls: "bg-amber-100 text-amber-800 border border-amber-200", icon: HardHat, desc: "Solo ve su propia ficha (portal)" },
};

const EMPTY = { email: "", name: "", password: "", role: "usuario", employee_id: "" };

export default function UsersManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [emps, setEmps] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  useEffect(() => { load(); api.get("/employees").then((r) => setEmps(r.data)).catch(() => {}); }, []);
  if (!users) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setDialog({ mode: "new" }); };
  const openEdit = (u) => { setForm({ email: u.email, name: u.name, password: "", role: u.role, employee_id: u.employee_id || "" }); setDialog({ mode: "edit", user: u }); };

  const save = async () => {
    setBusy(true);
    try {
      if (dialog.mode === "new") {
        await api.post("/users", form);
        toast.success("Usuario creado");
      } else {
        const body = { name: form.name, role: form.role, employee_id: form.employee_id };
        if (form.password) body.password = form.password;
        await api.put(`/users/${dialog.user.user_id}`, body);
        toast.success("Usuario actualizado");
      }
      setDialog(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar el usuario ${u.email}?`)) return;
    try { await api.delete(`/users/${u.user_id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeMyPassword = async () => {
    setBusy(true);
    try {
      await api.post("/auth/change-password", pwd);
      toast.success("Tu contraseña se ha actualizado");
      setPwd({ current_password: "", new_password: "" }); setDialog(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-4 sm:p-6" data-testid="users-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Users className="h-5 w-5 text-accent" />
        <h2 className="font-display text-lg font-bold">Usuarios y accesos</h2>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setDialog({ mode: "pwd" })} data-testid="change-my-password-button">
            <KeyRound className="h-4 w-4 mr-1.5" /> Mi contraseña
          </Button>
          <Button size="sm" onClick={openNew} data-testid="add-user-button"><Plus className="h-4 w-4 mr-1.5" /> Nuevo usuario</Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Tu cuenta de administrador: <span className="font-mono">{me?.email}</span>. La contraseña se cambia con "Mi contraseña".
      </p>
      <div className="grid sm:grid-cols-3 gap-2 mb-4 text-xs">
        {Object.entries(ROLES).map(([k, r]) => (
          <div key={k} className="rounded-md border p-2 flex gap-2 items-start"><r.icon className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" />
            <div><p className="font-semibold">{r.label}</p><p className="text-muted-foreground">{r.desc}</p></div></div>
        ))}
      </div>
      <div className="border rounded-lg divide-y" data-testid="users-list">
        {users.map((u) => {
          const r = ROLES[u.role] || ROLES.usuario;
          const emp = emps.find((e) => e.id === u.employee_id);
          return (
            <div key={u.user_id} className="flex flex-wrap items-center gap-2 p-3" data-testid={`user-row-${u.user_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{u.name || u.email} {u.user_id === me?.user_id && <span className="text-xs text-muted-foreground">(tú)</span>}</p>
                <p className="text-xs text-muted-foreground truncate font-mono">{u.email}{u.auth_provider === "google" ? " · Google" : ""}{emp ? ` · ${emp.name}` : ""}</p>
              </div>
              <Badge className={`${r.cls} text-[11px]`}>{r.label}</Badge>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}><KeyRound className="h-4 w-4" /></Button>
              {u.user_id !== me?.user_id && (
                <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => del(u)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          {dialog?.mode === "pwd" ? (
            <>
              <DialogHeader><DialogTitle className="font-display">Cambiar mi contraseña</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Contraseña actual</Label>
                  <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="current-password-input" /></div>
                <div className="space-y-1.5"><Label>Nueva contraseña (mín. 6)</Label>
                  <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="new-password-input" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
                <Button onClick={changeMyPassword} disabled={busy || pwd.new_password.length < 6} data-testid="save-my-password-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar</Button>
              </DialogFooter>
            </>
          ) : dialog && (
            <>
              <DialogHeader><DialogTitle className="font-display">{dialog.mode === "new" ? "Nuevo usuario" : "Editar usuario"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input type="email" value={form.email} disabled={dialog.mode === "edit"} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" /></div>
                <div className="space-y-1.5"><Label>Nombre</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></div>
                <div className="space-y-1.5"><Label>Rol</Label>
                  <select className="h-10 w-full rounded-md border bg-white px-3 text-sm" value={form.role} onChange={(e) => set("role", e.target.value)}
                    disabled={dialog.mode === "edit" && dialog.user.user_id === me?.user_id} data-testid="user-role-select">
                    {Object.entries(ROLES).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
                  </select></div>
                {form.role === "trabajador" && (
                  <div className="space-y-1.5"><Label>Empleado vinculado</Label>
                    <select className="h-10 w-full rounded-md border bg-white px-3 text-sm" value={form.employee_id} onChange={(e) => set("employee_id", e.target.value)} data-testid="user-employee-select">
                      <option value="">— Selecciona —</option>
                      {emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select></div>
                )}
                <div className="space-y-1.5"><Label>{dialog.mode === "new" ? "Contraseña (mín. 6)" : "Nueva contraseña (dejar vacío para no cambiar)"}</Label>
                  <Input type="text" autoComplete="off" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)}>Cancelar</Button>
                <Button onClick={save} disabled={busy || (dialog.mode === "new" && form.password.length < 6)} data-testid="save-user-button">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
